import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

export default function Home() {
  const navigate = useNavigate();
  const [avaliacoes, setAvaliacoes] = useState([]);
  const [turmas, setTurmas] = useState({}); // Vai guardar um dicionário { idDaTurma: 'Nome da Turma' }
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      const professorId = localStorage.getItem('usuarioId');
      if (!professorId) {
        setLoading(false);
        return;
      }

      // 1. Busca as avaliações
      const responseAvaliacoes = await api.get(`/avaliacoesobjetivas/professor/${professorId}`);
      const dadosAvaliacoes = responseAvaliacoes.data;
      
      setAvaliacoes(dadosAvaliacoes);

      // 2. Extrai os IDs únicos das turmas dessas avaliações
      const turmasIds = [...new Set(dadosAvaliacoes.map(a => a.turma_id))];

      // 3. Busca o nome de cada turma (se existirem turmas)
      if (turmasIds.length > 0) {
        const nomesTurmas = {};
        // Faz requisições em paralelo para buscar os nomes
        await Promise.all(turmasIds.map(async (id) => {
          try {
            // Confirme se a sua rota de buscar turma por ID é /turmas/:id
            const resTurma = await api.get(`/turmas/${id}`);
            nomesTurmas[id] = resTurma.data.nome; // Supondo que o retorno tenha o campo "nome"
          } catch (err) {
            console.error(`Erro ao buscar nome da turma ${id}:`, err);
            nomesTurmas[id] = "Turma não encontrada";
          }
        }));
        setTurmas(nomesTurmas);
      }

    } catch (error) {
      console.error("Erro ao carregar dados:", error);
    } finally {
      setLoading(false);
    }
  };

  const extrairData = (dataISO) => dataISO ? dataISO.substring(0, 10) : "";
  
  const getTodayLocal = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const todayStr = getTodayLocal();

  const avaliacoesHoje = useMemo(() => {
    return avaliacoes.filter(item => {
      const dataItem = extrairData(item.data);
      return dataItem === todayStr && item.status !== "concluida";
    });
  }, [avaliacoes, todayStr]);

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  if (loading) {
    return (
      <div style={styles.center}>
        <p>Carregando avaliações...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Painel do Professor</h2>
        <button onClick={handleLogout} style={styles.logoutBtn}>
          Sair
        </button>
      </div>
      
      {avaliacoesHoje.length > 0 ? (
        <>
          <h3 style={styles.subtitle}>Avaliações agendadas para hoje:</h3>
          {avaliacoesHoje.map(avaliacao => {
            // Pega o nome da turma do nosso "dicionário", ou mostra um fallback
            const nomeDaTurma = turmas[avaliacao.turma_id] || "Carregando turma...";

            return (
              <div 
                key={avaliacao.id}
                style={styles.alertContainer} 
                onClick={() => navigate('/alunos', { 
                  state: { 
                    turmaId: avaliacao.turma_id, 
                    nomeTurma: nomeDaTurma, // Agora envia o nome real para a próxima tela!
                    avaliacaoId: avaliacao.id,
                    qtdQuestoes: avaliacao.qtd_questoes,
                    qtdAlternativas: avaliacao.qtd_alternativas 
                  }
                })}
              >
                <div style={styles.alertHeaderRow}>
                  <h4 style={styles.alertTitle}>📢 {avaliacao.titulo.toUpperCase()}</h4>
                  {/* Badge com o nome da turma */}
                  <span style={styles.turmaBadge}>{nomeDaTurma}</span>
                </div>
                
                <p style={styles.alertText}>{avaliacao.descricao || 'Sem descrição'}</p>
                <p style={styles.alertAction}>Clique para selecionar o aluno e preencher o gabarito →</p>
              </div>
            );
          })}
        </>
      ) : (
        <div style={styles.emptyContainer}>
          <p style={styles.emptyText}>Você não tem nenhuma avaliação pendente para hoje. 🎉</p>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { 
    maxWidth: '600px', 
    margin: '0 auto', 
    padding: '20px' 
  },
  center: { 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    minHeight: '80vh' 
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
  },
  title: { 
    fontSize: '24px', 
    margin: 0, 
    color: '#333' 
  },
  logoutBtn: {
    backgroundColor: 'transparent',
    border: '1px solid #dc3545',
    color: '#dc3545',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 'bold',
    transition: 'all 0.2s ease'
  },
  subtitle: { 
    fontSize: '16px', 
    marginBottom: '15px', 
    color: '#555',
    fontWeight: 'normal'
  },
  alertContainer: { 
    backgroundColor: '#e8f5e9', 
    borderLeft: '5px solid #3f8c00', 
    padding: '15px', 
    borderRadius: '8px', 
    marginBottom: '15px', 
    cursor: 'pointer',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
  },
  alertHeaderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '8px'
  },
  alertTitle: { 
    color: '#1b5e20', 
    margin: 0,
    fontSize: '16px',
    flex: 1
  },
  turmaBadge: {
    backgroundColor: '#c8e6c9',
    color: '#2e7d32',
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 'bold',
    marginLeft: '10px'
  },
  alertText: { 
    color: '#2e7d32', 
    margin: '0 0 10px 0',
    fontSize: '14px'
  },
  alertAction: { 
    color: '#1b5e20', 
    fontSize: '13px', 
    fontWeight: 'bold', 
    margin: 0 
  },
  emptyContainer: {
    backgroundColor: '#fff',
    padding: '40px 20px',
    borderRadius: '12px',
    textAlign: 'center',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
  },
  emptyText: {
    color: '#666',
    fontSize: '16px',
    margin: 0
  }
};